import React, { useContext, useState } from 'react';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'routes';
import { isKiosk } from 'utils/platform';
import { getQuery } from 'utils/queryUtils';

export enum MiniSurveyStateEnum {
  default = 'default',
  success = 'success'
}

const HAS_SEEN_USER_SERVEY = 'has-seen-user-survey';

const SURVEY_TIMEOUT = 5;

const getTimeout = () => {
  const timeoutOverride = getQuery().get('userSurveyTimeout');
  if (timeoutOverride) {
    return parseInt(timeoutOverride, 10);
  }

  return SURVEY_TIMEOUT * 1000;
};

export const useTimedSurvey = () => {
  const history = useHistory();
  const {
    visible,
    wasShown,
    setVisible,
    surveyState,
    onCloseSurvey,
    onSelectSurvey,
    resetSurvey
  } = useSurvey();

  const hasSeenUserSurvey = () =>
    isKiosk() ? wasShown : localStorage.getItem(HAS_SEEN_USER_SERVEY) === 'true';
  const setHasSeenUserSurvey = () =>
    !isKiosk() && localStorage.setItem(HAS_SEEN_USER_SERVEY, 'true');

  const checkAndSetVisible = () => {
    if (history.location.pathname === AppRoutes.SUMMARY && !hasSeenUserSurvey) {
      setVisible();
    }
  };

  useEffect(() => {
    const timeoutInMs = getTimeout();
    let timeout: NodeJS.Timeout;
    if (history.location.pathname === AppRoutes.PLANNER && visible) {
      resetSurvey(true);
    }
    if (history.location.pathname === AppRoutes.SUMMARY && !hasSeenUserSurvey()) {
      if (wasShown) {
        checkAndSetVisible();
      } else {
        timeout = setTimeout(() => {
          checkAndSetVisible();
        }, timeoutInMs);
      }
    }
    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname, wasShown]);

  return {
    visible,
    setVisible,
    hasSeenUserSurvey,
    setHasSeenUserSurvey,
    surveyState,
    onCloseSurvey,
    onSelectSurvey
  };
};

type SurveyContextType = {
  visible: boolean;
  wasShown: boolean;
  surveyState: MiniSurveyStateEnum;
  setVisible: () => void;
  onCloseSurvey: (callback: () => void) => void;
  onSelectSurvey: (callback: () => void) => void;
  resetSurvey: (wasShown?: boolean) => void;
};

export const SurveyContext = React.createContext<SurveyContextType | null>(null);

export const useSurvey = () => {
  const context = useContext(SurveyContext);
  if (!context) {
    throw new Error(`Survey context must be defined`);
  }
  return context;
};

type Props = {
  children: React.ReactNode;
};

export const SurveyProvider = ({ children }: Props) => {
  const [visible, setSurveyVisible] = useState(false);
  const [wasShown, setWasShown] = useState(false);
  const [surveyState, setSurveyState] = useState<MiniSurveyStateEnum>(MiniSurveyStateEnum.default);

  const setVisible = () => {
    setSurveyState(MiniSurveyStateEnum.default);
    if (!wasShown) {
      setSurveyVisible(true);
    }
  };

  const onCloseSurvey = (callback: () => void) => {
    setSurveyVisible(false);
    if (isKiosk()) {
      setWasShown(true);
    }
    if (surveyState === MiniSurveyStateEnum.default) {
      callback();
    }
  };

  const onSelectSurvey = (callback: () => void) => {
    setSurveyState(MiniSurveyStateEnum.success);
    callback();
  };

  const resetSurvey = (wasShown?: boolean) => {
    setWasShown(wasShown ? true : false);
    setSurveyVisible(false);
  };

  /**
   * If the user has answered a survey, we hide the
   * "thank you" message after a few seconds
   */
  useEffect(() => {
    if (surveyState === MiniSurveyStateEnum.success) {
      setTimeout(() => {
        setSurveyVisible(false);
      }, 5000);
    }
  }, [surveyState]);

  return (
    <SurveyContext.Provider
      value={{
        visible,
        wasShown,
        surveyState,
        setVisible,
        onCloseSurvey,
        onSelectSurvey,
        resetSurvey
      }}
    >
      {children}
    </SurveyContext.Provider>
  );
};
